import React, { useState } from "react";
import {
  TextField,
  Box,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Icon } from "@iconify/react";
import  VisibilityOffIcon  from '@mui/icons-material/VisibilityOff';
import  VisibilityIcon  from '@mui/icons-material/Visibility';
import { stripEmojis } from "../../../utils/emoji";

const FormTextField = ({
  label,
  labelAction,
  name,
  register,
  errors,
  required = false,
  placeholder,
  disabled = false,
  value,
  onChange,
  onBlur,
  limit,
  labelColor = "var(--app-text-muted)",
  allowNumbers = true,
  allowSpecialChars = true,
  preventOnlySpaces = false,
  preventConsecutiveSpecial = false,
  type = "text",
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const getNestedError = (obj, path) => {
    if (!obj || !path) return undefined;
    return path.split(".").reduce((acc, key) => acc?.[key], obj);
  };

  const resolvedError =
    typeof errors === "string"
      ? { message: errors }
      : name
      ? getNestedError(errors, name)
      : errors;

  const isRHF = !!(register && name);
  const registration = isRHF ? register(name) : null;

  // Passwords are matched against what is already stored, so they are left as
  // typed; every other field drops emojis.
  const clean = (val) => (type === "password" ? val : stripEmojis(val));

  // react-hook-form reads the value off the event, so cleaning the DOM value
  // before handing it over is enough for the uncontrolled path.
  const handleRegisterChange = (e) => {
    const val = clean(e.target.value);
    if (val !== e.target.value) e.target.value = val;
    return registration.onChange(e);
  };

  const handleChange = (e) => {
    let val = clean(e.target.value);

    // Prevent starting space
    if (val.startsWith(" ")) {
      val = val.trimStart();
    }

    // Prevent consecutive special characters like @@
    if (preventConsecutiveSpecial) {
      val = val.replace(/([@#$%^&*])\1+/g, "$1");
    }

    // Prevent numbers if not allowed
    if (!allowNumbers) {
      val = val.replace(/[0-9]/g, "");
    }

    // Prevent special characters if not allowed
    if (!allowSpecialChars) {
      val = val.replace(/[^a-zA-Z\s]/g, "");
    }

    // Prevent only spaces
    if (preventOnlySpaces && val.trim() === "") {
      val = "";
    }

    // Limit length
    // if (limit) {
    //   val = val.slice(0, limit);
    // }

    onChange?.({
      ...e,
      target: {
        ...e.target,
        value: val,
      },
    });
  };

  return (
    <Box>
      {(label || labelAction) && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: "6px",
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 500,
              color: labelColor,
              fontFamily: "Figtree",
            }}
          >
            {label}
            {required && <span style={{ color: "#d32f2f" }}> *</span>}
          </Typography>

          {labelAction && <Box>{labelAction}</Box>}
        </Box>
      )}

      <TextField
        fullWidth
        variant="filled"
        hiddenLabel
        disableUnderline
        type={
          type === "password"
            ? showPassword
              ? "text"
              : "password"
            : type
        }
        {...(isRHF ? { ...registration, onChange: handleRegisterChange } : {})}
        {...(!isRHF
          ? {
              value: value ?? "",
              onChange: handleChange,
              onBlur,
            }
          : {})}
        error={!!resolvedError}
        helperText={resolvedError?.message || ""}
        disabled={disabled}
        placeholder={placeholder}
       InputProps={{
  disableUnderline: true,
  endAdornment:
    type === "password" ? (
      <InputAdornment position="end">
        <IconButton
          onClick={() => setShowPassword((prev) => !prev)}
          edge="end"
          disabled={disabled}
          sx={{
            p: "6px",
            mr: "2px",
          }}
        >
          {showPassword ? (
            <VisibilityOffIcon fontSize="small" />
          ) : (
            <VisibilityIcon fontSize="small" />
          )}
        </IconButton>
      </InputAdornment>
    ) : null,

  sx: {
    minHeight: "48px", // use minHeight instead of fixed height
    borderRadius: "8px",
    backgroundColor: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    px: "12px", // increase padding
    fontFamily: "Figtree",
    display: "flex",
    alignItems: "center",

    "& .MuiInputBase-input": {
      fontSize: "14px",
      fontWeight: 400,
      color: "var(--app-text)",
      padding: "12px 0",
    },

    "& .MuiInputBase-input::placeholder": {
      fontSize: "14px",
      fontWeight: 400,
      color: "var(--app-text-muted)",
      opacity: 1,
    },

    "& .MuiInputAdornment-root": {
      marginLeft: "8px",
    },

    "&:hover": {
      backgroundColor: "var(--app-surface)",
    },

    "&.Mui-focused": {
      backgroundColor: "var(--app-surface)",
    },

    "&.Mui-disabled": {
      backgroundColor: "var(--app-surface-2)",
      border: "1px solid var(--app-border)",
      opacity: 1,
      cursor: "not-allowed",
    },

    "&.Mui-disabled .MuiInputBase-input": {
      color: "var(--app-text-muted)",
      WebkitTextFillColor: "var(--app-text-muted)",
    },
  },
}}
        // inputProps={{
        //   maxLength: limit,
        // }}
        sx={{
          cursor: disabled ? "not-allowed" : "text",

          "& .MuiFilledInput-root:before, & .MuiFilledInput-root:after": {
            display: "none",
          },

          "& .MuiFilledInput-root:hover:not(.Mui-disabled):before": {
            display: "none",
          },
        }}
      />
    </Box>
  );
};

export default FormTextField;